// TMOS Issue Categories Management Page
// Date: November 16, 2025
// Purpose: Platform Administrator page for managing global issue categories

import React, { useState } from 'react';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { ViewModal } from '@/components/common/ViewModal';
import { EditModal } from '@/components/common/EditModal';
import { issueCategoryEntity } from '@/config/entities/issueCategories';
import { createCRUDService } from '@/services/genericCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export const IssueCategoriesPage: React.FC = () => {
  const { t } = useLanguage();
  const issueCategoryService = createCRUDService(issueCategoryEntity);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [viewingCategory, setViewingCategory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Category handlers
  const handleViewCategory = (category: any) => {
    setViewingCategory(category);
    setShowViewModal(true);
  };

  const handleCreateCategory = () => {
    setShowCreateModal(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const handleDeleteCategory = async (category: any) => {
    if (window.confirm(t('issues.categories.confirmDelete') || `Are you sure you want to delete category "${category.name}"?`)) {
      try {
        await issueCategoryService.delete(category.id);
        // Table will automatically refresh
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(t('issues.categories.deleteError') || 'Failed to delete category');
      }
    }
  };

  const handleCreateCategorySubmit = async (data: any) => {
    setLoading(true);
    try {
      await issueCategoryService.create(data);
      setShowCreateModal(false);
      // Table will automatically refresh
    } catch (error) {
      console.error('Error creating category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategorySubmit = async (data: any) => {
    setLoading(true);
    try {
      await issueCategoryService.update(editingCategory.id, data);
      setShowEditModal(false);
      setEditingCategory(null);
      // Table will automatically refresh
    } catch (error) {
      console.error('Error updating category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCategory = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setEditingCategory(null);
    setViewingCategory(null);
  };

  return (
    <div className="issue-categories-page">
      <div className="bg-white shadow">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('issues.categories.title') || 'Issue Categories'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('issues.categories.description') || 'Manage global issue categories available across all tenants'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <FlexibleCRUDTable
          entity={issueCategoryEntity}
          service={issueCategoryService}
          onView={handleViewCategory}
          onEdit={handleEditCategory}
          onDelete={handleDeleteCategory}
          onCreate={handleCreateCategory}
          searchPlaceholder={t('issues.categories.searchPlaceholder') || 'Search categories...'}
          showPagination={true}
          showSearch={true}
          showCreateButton={true}
        />
      </div>

      {/* Category Modals */}
      {showCreateModal && (
        <EditModal
          isOpen={showCreateModal}
          onClose={handleCancelCategory}
          title={t('issues.categories.createTitle') || `Create ${issueCategoryEntity.displayNameSingular}`}
          entity={issueCategoryEntity}
          onSubmit={handleCreateCategorySubmit}
          onCancel={handleCancelCategory}
          loading={loading}
        />
      )}

      {showEditModal && editingCategory && (
        <EditModal
          isOpen={showEditModal}
          onClose={handleCancelCategory}
          title={t('issues.categories.editTitle') || `Edit ${issueCategoryEntity.displayNameSingular}`}
          entity={issueCategoryEntity}
          initialData={editingCategory}
          onSubmit={handleEditCategorySubmit}
          onCancel={handleCancelCategory}
          loading={loading}
        />
      )}

      {showViewModal && viewingCategory && (
        <ViewModal
          isOpen={showViewModal}
          onClose={handleCancelCategory}
          title={t('issues.categories.viewTitle') || `View ${issueCategoryEntity.displayNameSingular}`}
          data={viewingCategory}
          fields={issueCategoryEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}
    </div>
  );
};

